import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserManagement.css';

const ROLES = [
  { value: 'municipality_admin', label: 'Municipality Admin' },
  { value: 'license_clerk', label: 'License Clerk' },
  { value: 'verifier', label: 'Document Verifier' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'approver', label: 'Approver' },
  { value: 'account_officer', label: 'Account Officer' },
  { value: 'report_viewer', label: 'Report Viewer' }
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        '/api/trade-license/permission-roles/',
        { withCredentials: true }
      );
      setUsers(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await axios.patch(
          `/api/trade-license/permission-roles/${selectedUser.id}/`,
          formData,
          { withCredentials: true }
        );
        alert('User updated successfully!');
      } else {
        await axios.post(
          '/api/trade-license/permission-roles/',
          formData,
          { withCredentials: true }
        );
        alert('User created successfully!');
      }
      setFormData({ username: '', email: '', first_name: '', last_name: '', role: '', is_active: true });
      setSelectedUser(null);
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      alert('Error saving user: ' + error.message);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.user_username,
      email: user.user_email,
      first_name: user.user_first_name,
      last_name: user.user_last_name,
      role: user.role,
      is_active: user.is_active
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedUser(null);
    setFormData({ username: '', email: '', first_name: '', last_name: '', role: '', is_active: true });
  };

  return (
    <div className="user-management-container">
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '+ Add New User'}
        </button>
      </div>

      {showForm && (
        <div className="user-form-card">
          <h2>{selectedUser ? 'Edit User' : 'Create New User'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={selectedUser ? true : false}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a role</option>
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {selectedUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : users.length > 0 ? (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.user_username}</strong>
                  </td>
                  <td>{user.user_email}</td>
                  <td>{user.user_first_name} {user.user_last_name}</td>
                  <td>{ROLES.find(r => r.value === user.role)?.label}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => handleEditUser(user)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p>No users found. Create one to get started.</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;